<template>
  <div>
    <div class="base-slider">
      <button
        v-if="items.length > 1 && currentIndex > 0"
        class="base-slider__nav base-slider__nav--prev"
        @click="handlePrev"
      >
        <div>&#10094;</div>
      </button>
  
      <div
        class="base-slider__list"
        @touchstart="handleStart"
        @touchmove="handleMove"
        @touchend="handleEnd"
        @mousedown="handleStart"
        @mousemove="handleMove"
        @mouseup="handleEnd"
        @mouseleave="handleEnd"
      >
        <div
          class="base-slider__track"
          :class="trackClass"
          :style="trackStyle"
        >
          <div
            v-for="(item, index) in items"
            :key="item.hash_id || index"
            class="base-slider__slide"
            :class="slideClass"
          >
            <slot
              name="item"
              :item="item"
              :index="index"
            />
          </div>
        </div>
      </div>
  
      <button
        v-if="items.length > 1 && currentIndex < items.length - 1"
        class="base-slider__nav base-slider__nav--next"
        @click="handleNext"
      >
        <div>&#10095;</div>
      </button>
    </div>

    <div
      v-if="showPagination && items.length > 1"
      class="base-slider__pagination--info"
    >
      {{ currentIndex + 1 }} / {{ items.length }}
    </div>

    <div
      v-if="showPagination && items.length > 1"
      class="base-slider__pagination"
    >
      <span
        v-for="(_, index) in items"
        :key="index"
        class="base-slider__pagination--dot"
        :class="{ active: index === currentIndex }"
        @click="currentIndex = index"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseSlider',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    trackClass: {
      type: [String, Array, Object],
      default: ''
    },
    slideClass: {
      type: [String, Array, Object],
      default: ''
    },
    showPagination: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentIndex: 0,
      startX: 0,
      currentX: 0,
      isDragging: false,
      isMobile: false,
    };
  },
  computed: {
    trackStyle() {
      if (!this.isMobile) return {};
      const dragOffset = this.isDragging
        ? this.currentX - this.startX
        : 0;
      return {
        transform: `translate3d(calc(-${this.currentIndex * 100}% + ${dragOffset}px),0,0)`,
        transition: this.isDragging
          ? 'none'
          : 'transform .45s cubic-bezier(0.22, 1, 0.36, 1)',
      };
    },
  },
  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleNext() {
      if (
        this.isMobile &&
        this.currentIndex < this.items.length - 1
      ) {
        this.currentIndex++;
      }
    },
    handlePrev() {
      if (
        this.isMobile &&
        this.currentIndex > 0
      ) {
        this.currentIndex--;
      }
    },
    handleStart(e) {
      if (!this.isMobile) return;
      this.isDragging = true;
      this.startX = e.touches?.[0]?.clientX || e.clientX;
      this.currentX = this.startX;
    },
    handleMove(e) {
      if (!this.isMobile || !this.isDragging) return;
      this.currentX = e.touches?.[0]?.clientX || e.clientX;
    },
    handleEnd() {
      if (!this.isMobile || !this.isDragging) return;
      const distance = this.currentX - this.startX;
      const threshold = 50;
      if (distance > threshold) {
        this.handlePrev();
      } else if (distance < -threshold) {
        this.handleNext();
      }
      this.isDragging = false;
    },
    handleResize() {
      const isMobile = window.innerWidth < 768;
      if (this.isMobile !== isMobile) {
        this.currentIndex = 0;
        this.isDragging = false;
      }
      this.isMobile = isMobile;
    },
  },
};
</script>

<style scoped lang="scss">
.base-slider {
  position: relative;
  word-break: break-word;
  word-wrap: break-word;
  white-space: normal;
  &__list {
    overflow: hidden;
    width: 100%;
  }
  @media screen and (max-width: 767px) {
    &__track {
      display: flex;
      will-change: transform;
    }
    &__slide {
      flex: 0 0 100%;
      min-width: 100%;
      padding: 24px;
    }
    &__nav {
      min-width: 30px;
      height: 30px;
      color: #1976d2;
      background: #fff;
      border-radius: 50%;
      border: 1px solid #e5e7eb;
      box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      > div {
        margin-bottom: 2px;
      }
      &--prev {
        left: -15px;
        > div {
          margin-right: 1px;
        }
      }
      &--next {
        right: -15px;
        > div {
          margin-left: 1px;
        }
      }
    }
    &__pagination {
      display: flex;
      justify-content: center;
      gap: 15px;
      margin-top: 20px;
      &--info {
        margin-top: 20px;
        text-align: center;
      }
      &--dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #d1d5db;
        cursor: pointer;
        &.active {
          background: #1976d2;
        }
      }
    }
  }
  @media screen and (min-width: 768px) {
    &__nav,
    &__pagination,
    &__pagination--info {
      display: none;
    }
  }
}
</style>
